import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { fetchProfile } from '../services/moderators';
import { compareModerators } from '../helpers/profile';
import { ModeratorResponse } from '../types/api/moderators';
import { CountryResponse } from '../types/api/common';

interface UserContextType {
    userUuid: string | null;
    setUserUuid: (uuid: string | null) => void;
    jwtToken: string | null;
    setJwtToken: (token: string | null) => void;
    jwtRefreshToken: string | null;
    setJwtRefreshToken: (token: string | null) => void;
    lastPageUrl: string | null;
    setIsLoadingProfile: (val: boolean) => void;
    isLoadingProfile: boolean | null;
    setLastPageUrl: (value: string | null) => void;
    profile: ModeratorResponse | null;
    setProfile: (val: ModeratorResponse) => void;
    country: CountryResponse | null;
    setCountry: (val: CountryResponse | null) => void;
    isFetchingSetup: boolean
}

const UserContext = createContext<UserContextType>({
    userUuid: null,
    setUserUuid: () => {
    },
    jwtToken: null,
    setJwtToken: () => {
    },
    jwtRefreshToken: null,
    setJwtRefreshToken: () => {
    },
    lastPageUrl: null,
    setLastPageUrl: () => {
    },
    isLoadingProfile: null,
    setIsLoadingProfile: () => {
    },
    profile: null,
    setProfile: () => {
    },
    country: null,
    setCountry: () => {
    },
    isFetchingSetup: false
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [jwtToken, setJwtToken] = useState<string | null>(() => {
        return localStorage.getItem('xAuthToken');
    });
    const [jwtRefreshToken, setJwtRefreshToken] = useState<string | null>(() => {
        return localStorage.getItem('xAuthRefreshToken');
    });
    const [userUuid, setUserUuid] = useState<string | null>(() => {
        return localStorage.getItem('xProfilePk');
    });
    const [profile, setProfile] = useState<ModeratorResponse | null>(() => {
        const profileData = localStorage.getItem('xProfile');
        return profileData ? JSON.parse(profileData) : null;
    });

    const [country, setCountry] = useState<CountryResponse | null>(() => {
        const countryData = localStorage.getItem('xCountry');
        return countryData ? JSON.parse(countryData) : null;
    });

    const [lastPageUrl, setLastPageUrl] = useState<string | null>(null);

    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const isFromPageReload = useRef(false);

    const { mutate: fetchProfileMutation, isPending: isFetchingSetup } = useMutation({
        mutationFn: fetchProfile,
        onSuccess: (data) => {
            if (profile) {
                let isEqualNowAndOldProfiles = compareModerators(profile, data)
                console.log('isEqualNowAndOldProfiles', isEqualNowAndOldProfiles)
                if (isEqualNowAndOldProfiles) {
                    saveProfile(data)
                    tryGoToMainPage()
                } else {
                    // В django админке внесли изменения в профиль, поэтому сохранить и все обновить 
                    saveProfile(data)
                    window.location.reload();
                }
            } else {
                saveProfile(data)
                tryGoToMainPage()
            }
        },
        onError: (error: any) => {
            setIsLoadingProfile(false)
            isFromPageReload.current = false;
            // Дальше за логику отвечает RoleBasedRedirect
        }
    });

    const saveProfile = (profile: ModeratorResponse) => {
        setProfile(profile);
        setIsLoadingProfile(false);
        isFromPageReload.current = false;
    }

    const tryGoToMainPage = () => {
        if (!isFromPageReload.current) {
            window.location.replace('/');
        }
    }

    useEffect(() => {
        if (isLoadingProfile) {
            fetchProfileMutation();
        }
    }, [isLoadingProfile]);

    useEffect(() => {
        if (jwtToken) {
            localStorage.setItem('xAuthToken', jwtToken);
        } else {
            localStorage.removeItem('xAuthToken');
        }
    }, [jwtToken]);

    useEffect(() => {
        if (jwtRefreshToken) {
            localStorage.setItem('xAuthRefreshToken', jwtRefreshToken);
        } else {
            localStorage.removeItem('xAuthRefreshToken');
        }
    }, [jwtRefreshToken]);

    useEffect(() => {
        if (userUuid) {
            localStorage.setItem('xProfilePk', userUuid);
        } else {
            localStorage.removeItem('xProfilePk');
        }
    }, [userUuid]);

    useEffect(() => {
        if (profile) {
            localStorage.setItem('xProfile', JSON.stringify(profile));
        } else {
            localStorage.removeItem('xProfile');
        }
    }, [profile]);

    useEffect(() => {
        if (country) {
            localStorage.setItem('xCountry', JSON.stringify(country));
        } else {
            localStorage.removeItem('xCountry');
        }
    }, [country]);

    useEffect(() => {
        const navigationType = (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.type;
        const isDirectAccess = !document.referrer || document.referrer === window.location.origin;

        if (process.env.NODE_ENV === 'development') {
            return;
        }

        if (navigationType === 'reload' || (navigationType === 'navigate' && isDirectAccess)) {
            isFromPageReload.current = true;
            setIsLoadingProfile(true);
        }
    }, []);

    return (
        <UserContext.Provider
            value={{
                jwtToken, setJwtToken,
                jwtRefreshToken, setJwtRefreshToken,
                userUuid, setUserUuid,
                lastPageUrl, setLastPageUrl,
                isLoadingProfile,
                setIsLoadingProfile,
                profile,
                setProfile,
                isFetchingSetup,
                country,
                setCountry
            }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('Использование useUserContext должно быть внутри связанного с ним провайдера UserProvider');
    }
    return context;
};